// Central route registry for the app.
// This is the single source of truth for:
// - which paths are valid
// - which paths are public/auth/store/user scoped
// - whether the store account sidebar should show

type RouteGroup = 'public' | 'auth' | 'store' | 'user' | 'admin';

type RouteMeta = {
  placeholder?: boolean;
  authBypass?: boolean;
  sidebar?: string;
  virtual?: boolean;
};

type ExactRoute = {
  id: string;
  kind: 'exact';
  path: string;
  group: RouteGroup;
  meta: RouteMeta;
};

type PrefixRoute = {
  id: string;
  kind: 'prefix';
  prefix: string;
  group: RouteGroup;
  meta: RouteMeta;
};

type RouteDef = ExactRoute | PrefixRoute;

const makeExact = (id: string, path: string, group: RouteGroup, meta: RouteMeta = {}): ExactRoute => ({
  id,
  kind: 'exact',
  path,
  group,
  meta
});

const makePrefix = (id: string, prefix: string, group: RouteGroup, meta: RouteMeta = {}): PrefixRoute => ({
  id,
  kind: 'prefix',
  prefix,
  group,
  meta
});

export const ROUTES = [
  // Public marketing pages
  makeExact('home', '/', 'public'),
  makeExact('pricing', '/pricing', 'public'),
  makeExact('pricing_enterprise', '/pricing/enterprise', 'public'),
  makeExact('pricing_users', '/pricing/users', 'public'),
  makeExact('blog', '/blog', 'public'),
  makeExact('support', '/support', 'public'),
  makeExact('docs', '/docs', 'public', { placeholder: true }),
  makeExact('changelog', '/changelog', 'public', { placeholder: true }),
  makeExact('community', '/community', 'public', { placeholder: true }),
  makeExact('not_found', '/404', 'public', { placeholder: true }),

  // Auth
  makeExact('login', '/login', 'auth', { authBypass: true }),
  makeExact('register', '/register', 'auth', { authBypass: true }),
  makePrefix('register_prefix', '/register/', 'auth', { authBypass: true }),

  // Store: dashboard + manage
  makeExact('store_root', '/store', 'store'),
  makeExact('store_upgrade', '/store/upgrade', 'store', { sidebar: 'account' }),
  makeExact('store_profile', '/store/profile', 'store', { sidebar: 'account' }),
  makeExact('store_billing', '/store/billing', 'store', { sidebar: 'account' }),
  makeExact('store_settings', '/store/settings', 'store', { sidebar: 'account' }),
  makeExact('store_privacy', '/store/privacy', 'store', { sidebar: 'account' }),
  makeExact('store_terms', '/store/terms', 'store', { sidebar: 'account' }),
  makeExact('store_refund', '/store/refund', 'store', { sidebar: 'account' }),
  makeExact('store_shipping', '/store/shipping', 'store', { sidebar: 'account' }),
  makeExact('store_contact', '/store/contact', 'store', { sidebar: 'account' }),
  makeExact('store_memberships', '/store/memberships', 'store'),
  makePrefix('store_memberships_prefix', '/store/memberships/', 'store'),

  makeExact('store_manage', '/store/manage', 'store'),
  makeExact('store_manage_sections', '/store/manage/sections', 'store'),
  makeExact('store_manage_queue', '/store/manage/queue', 'store'),
  makeExact('store_manage_hours', '/store/manage/hours', 'store'),

  // User dashboard
  makeExact('user_root', '/user', 'user'),

  // Product detail pages
  makeExact('product_queue', '/queue', 'public'),
  makeExact('product_analytics', '/analytics', 'public'),
  makeExact('product_loyalty', '/loyalty', 'public'),
  makeExact('product_notify', '/notify', 'public'),
  makeExact('product_reserve', '/reserve', 'public'),
  makeExact('product_ads', '/ads', 'public'),
  makeExact('product_flow', '/flow', 'public'),
  makeExact('product_connect', '/connect', 'public'),
  makeExact('product_enterprise', '/enterprise', 'public'),
  makeExact('product_pass', '/pass', 'public'),


  // Admin portal
  makeExact('admin_login', '/admin/login', 'admin', { authBypass: true }),
  makeExact('admin_root', '/admin', 'admin'),
  makePrefix('admin_prefix', '/admin/', 'admin'),

  // Safety net: classify any /store/manage/* as store-scoped for guards/bootloader,
  // but still treat unknown subpaths as Not Found (meta.virtual).
  makePrefix('store_manage_prefix', '/store/manage/', 'store', { virtual: true })
];

const normalizePath = (raw: string | null | undefined): string => {
  const path = String(raw || '/').split('#')[0].split('?')[0] || '/';
  if (!path.startsWith('/')) {
    return `/${path}`;
  }
  return path || '/';
};

const matchRoute = (def: RouteDef, path: string): boolean => {
  if (def.kind === 'exact') {
    return path === def.path;
  }
  return path.startsWith(def.prefix);
};

export const resolveRoute = (rawPath: string | null | undefined): RouteDef | null => {
  const path = normalizePath(rawPath);
  let best: { def: RouteDef; score: number } | null = null;
  for (const def of ROUTES) {
    if (!matchRoute(def, path)) {
      continue;
    }
    const score = def.kind === 'exact' ? 10_000 + def.path.length : def.prefix.length;
    if (!best || score > best.score) {
      best = { def, score };
    }
  }
  return best ? best.def : null;
};

export const isConcreteRoute = (path) => {
  const def = resolveRoute(path);
  return Boolean(def && !def?.meta?.virtual);
};

export const isValidRoute = (path) => Boolean(resolveRoute(path));

export const isStoreRoute = (path) => {
  const def = resolveRoute(path);
  return def?.group === 'store';
};

export const isPublicRoute = (path) => {
  const def = resolveRoute(path);
  return def?.group === 'public';
};

export const isAuthBypassRoute = (path) => {
  const def = resolveRoute(path);
  return Boolean(def?.meta?.authBypass);
};

export const isPlaceholderRoute = (path) => {
  const def = resolveRoute(path);
  return Boolean(def?.meta?.placeholder);
};

export const shouldShowStoreAccountSidebar = (path) => {
  const def = resolveRoute(path);
  return def?.group === 'store' && def?.meta?.sidebar === 'account';
};

export const PRODUCT_DETAIL_TYPES = [
  'queue',
  'analytics',
  'loyalty',
  'notify',
  'reserve',
  'ads',
  'flow',
  'connect',
  'enterprise',
  'pass'
];
